import React, { useState } from "react";
import AdminDashboardLayout from "../../../layout/AdminDashboardLayout";
import { Button, Form, message, Modal, Select, Table } from "antd";
import Moment from "react-moment";
import { useGetSubjectsQuery } from "../../../hooks/api/Admin/Courses/useGetSubjectsQuery";
import { useAddSubjectMutation } from "../../../hooks/api/Admin/Courses/useAddSubjectMutation";
import { useDeleteSubjectMutation } from "../../../hooks/api/Admin/Courses/useDeleteSubjectMutation";
import { CircleX } from "lucide-react";

interface Subject {
  _id: string;
  subject: string;
  addedBy?: string;
  createdAt: string;
  updatedAt: string;
}

const AdminSubjects = () => {
  const [open, setOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [payload, setPayload] = useState({ category: "", subject: "" });
  const [page, setPage] = useState(1);

  const { data: subjects, isLoading } = useGetSubjectsQuery({ page });
  const { mutateAsync: addSubject } = useAddSubjectMutation();
  const { mutateAsync: deleteSubject } = useDeleteSubjectMutation();

  const columns = [
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (subject: string) => (
        <span className="capitalize">{subject}</span>
      ),
    },
    {
      title: "Added By",
      dataIndex: "addedBy",
      key: "addedBy",
      render: (addedBy: string | undefined) => addedBy || "—",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => (
        <div>
          <Moment format="DD/MM/YYYY">{createdAt}</Moment>
          <br />
          <Moment format="h:mm a">{createdAt}</Moment>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: Subject) => (
        <button
          className="bg-red-500 text-[12px] text-white rounded-md p-2 hover:bg-red-600 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedSubject(record);
            setDeleteModalOpen(true);
          }}
        >
          Delete
        </button>
      ),
    },
  ];

  const handleDeleteConfirm = async () => {
    if (!selectedSubject?._id) {
      message.error("Invalid subject selected");
      return;
    }

    try {
      await deleteSubject({ id: selectedSubject._id });
      message.success("Subject deleted successfully");
      setDeleteModalOpen(false);
      setSelectedSubject(null);
    } catch (error) {
      message.error("Error deleting subject");
    }
  };

  const handleSubmit = async () => {
    try {
      await addSubject(payload);
      message.success("Subject added successfully");
      setOpen(false);
      setPayload({ category: "", subject: "" });
    } catch (error) {
      message.error("Error adding subject");
    }
  };

  const handleModalClose = () => {
    setOpen(false);
    setPayload({ category: "", subject: "" });
  };

  return (
    <AdminDashboardLayout>
      <div className="flex justify-between items-center mb-3">
        <h3 className="W2STBold text-[20px]">
          Courses ({subjects?.data?.meta?.totalDocs || 0})
        </h3>
        <button className="w2st-button text-tmd" onClick={() => setOpen(true)}>
          Add New Course
        </button>
      </div>

      <Table
        dataSource={Object.entries(subjects?.data?.categories || {})
          .map(([category, subjects]) => ({
            key: category,
            category,
            subjects,
          }))
          .sort((a, b) => {
            const order = [
              "Elementary K-5",
              "Middle School 6-8",
              "High School",
            ];
            return order.indexOf(a.category) - order.indexOf(b.category);
          })}
        columns={[
          {
            title: "Category",
            dataIndex: "category",
            key: "category",
            render: (text: string) => (
              <span className="font-medium">{text}</span>
            ),
          },
        ]}
        expandable={{
          expandedRowRender: (record) => (
            <Table
              columns={columns}
              dataSource={Array.isArray(record.subjects) ? record.subjects : []}
              pagination={false}
              className="nested-table"
              rowKey="_id"
              locale={{ emptyText: "No subjects in this category." }}
            />
          ),
          rowExpandable: (record) =>
            Array.isArray(record.subjects) && record.subjects.length > 0,
        }}
        scroll={{ y: "60vh" }}
        pagination={false}
        loading={isLoading}
        locale={{ emptyText: "No categories available." }}
      />

      <Modal
        open={open}
        onCancel={handleModalClose}
        footer={null}
        closable={false}
        width="400px"
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-[20px] W2STMedium">Add a Subject</h3>
          <CircleX
            size={20}
            className="cursor-pointer"
            onClick={handleModalClose}
          />
        </div>
        <div className="mt-4">
          <label className="block mb-2">Select Category</label>
          <Select
            placeholder="Select Category"
            onChange={(value) => setPayload({ ...payload, category: value })}
            value={payload.category}
            size="large"
            className="w-full"
          >
            <Select.Option value="" disabled>
              Select Option
            </Select.Option>
            <Select.Option value="Elementary K-5">Elementary K-5</Select.Option>
            <Select.Option value="Middle School 6-8">
              Middle School 6-8
            </Select.Option>
            <Select.Option value="High School">High School</Select.Option>
          </Select>
        </div>

        <div className="mt-4">
          <label className="block mb-2">Add a Subject</label>
          <input
            type="text"
            value={payload.subject}
            placeholder="Type Subject name here"
            className="p-4 rounded-md w-full bg-slate-100 capitalize"
            onChange={(e) =>
              setPayload({ ...payload, subject: e.target.value.toLowerCase() })
            }
          />
        </div>

        <button
          type="submit"
          onClick={handleSubmit}
          disabled={!payload.subject || !payload.category}
          className={`px-4 w-full text-white py-4 rounded-md mt-4 ${
            !payload.subject || !payload.category
              ? "bg-primary300 cursor-not-allowed"
              : "bg-primary800 cursor-pointer shadow-md hover:bg-primary900 transition-colors"
          }`}
        >
          Submit
        </button>
      </Modal>

      <Modal
        open={deleteModalOpen}
        onCancel={() => {
          setDeleteModalOpen(false);
          setSelectedSubject(null);
        }}
        onOk={handleDeleteConfirm}
        title="Confirm Delete"
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          className: "bg-red-500 hover:bg-red-600",
          loading: isLoading,
        }}
        className="max-w-[400px]"
      >
        <p>
          Are you sure you want to delete &quot;{selectedSubject?.subject}
          &quot;?
        </p>
      </Modal>
    </AdminDashboardLayout>
  );
};

export default AdminSubjects;
